import axiosInstance from '../axios/axios';
import {
  VendorReviewDecision,
  VendorReviewDecisionType,
  VendorMatch,
  VendorReviewWithMatches,
} from '../../types/vendor';
import axios from 'axios';

const VENDOR_REVIEW_ENDPOINT = 'api/vendor-review/decision';
const TABLE_API_URL = 'api/azure-table-operations';

export interface VendorReviewDecisionRequest {
  ClientId: string;
  ReviewId: string;
  Decision: VendorReviewDecisionType;
  SelectedVendorId?: string;
  NewVendorName?: string;
}

export interface VendorReviewServiceError {
  message: string;
  statusCode?: number;
}

// Raw entity from Azure Table Storage
interface VendorReviewTableEntity {
  PartitionKey: string;
  RowKey: string;
  ExtractedVendorName: string;
  VendorGST?: string;
  CompanyId?: string;
  FileName?: string;
  BatchId?: string;
  Status: string;
  MatchingVendorsJson?: string;
}

// PascalCase vendor match from JSON (backend format)
interface VendorMatchRaw {
  VendorId: string;
  VendorName: string;
  VendorGST?: string;
  CompanyId?: string;
  CompanyName?: string;
  SimilarityScore: number;
}

export const vendorReviewService = {
  async submitDecision(
    clientId: string,
    decision: VendorReviewDecision,
  ): Promise<void> {
    try {
      const request: VendorReviewDecisionRequest = {
        ClientId: clientId,
        ReviewId: decision.ReviewId,
        Decision: decision.Decision,
        SelectedVendorId: decision.SelectedVendorId,
        NewVendorName: decision.NewVendorName,
      };

      await axiosInstance.post(VENDOR_REVIEW_ENDPOINT, request);
    } catch (error: unknown) {
      const serviceError = handleVendorReviewError(error);
      throw serviceError;
    }
  },

  async getRequiredReviews(
    clientId: string,
    batchId: string,
  ): Promise<VendorReviewWithMatches[]> {
    try {
      const request = {
        tableName: 'VendorReview',
        operationName: 'getbyquery',
        query: `PartitionKey eq '${clientId.toLowerCase()}' and BatchId eq '${batchId}'`,
      };

      const response = await axiosInstance.post<VendorReviewTableEntity[]>(
        TABLE_API_URL,
        request,
      );

      // Count completed items for pagination
      const completedCount = response.data.filter(
        entity => entity.Status === 'Completed',
      ).length;

      // Filter out completed items and transform raw table data to VendorReviewWithMatches format
      const pendingReviews = response.data
        .filter(entity => entity.Status !== 'Completed')
        .map((entity, index) => {
          let matchingVendors: VendorMatch[] = [];
          if (entity.MatchingVendorsJson) {
            try {
              const rawMatches: VendorMatchRaw[] = JSON.parse(
                entity.MatchingVendorsJson,
              );
              // Transform PascalCase to camelCase
              matchingVendors = rawMatches.map(raw => ({
                vendorId: raw.VendorId,
                vendorName: raw.VendorName,
                vendorGST: raw.VendorGST,
                companyId: raw.CompanyId,
                companyName: raw.CompanyName,
                similarityScore: raw.SimilarityScore,
              }));
            } catch {
              console.warn(
                'Failed to parse MatchingVendorsJson for review',
                entity.RowKey,
              );
            }
          }

          return {
            reviewId: entity.RowKey,
            extractedVendorName: entity.ExtractedVendorName,
            vendorGST: entity.VendorGST,
            companyId: entity.CompanyId,
            fileName: entity.FileName,
            batchId: entity.BatchId,
            matchingVendors,
            currentPage: completedCount + index + 1,
            totalItems: response.data.length,
            pageSize: 1,
          };
        });

      return pendingReviews;
    } catch (error: unknown) {
      const serviceError = handleVendorReviewError(error);
      throw serviceError;
    }
  },
};
function handleVendorReviewError(error: unknown): VendorReviewServiceError {
  if (axios.isAxiosError(error)) {
    const statusCode = error.response?.status;

    if (statusCode === 400) {
      return {
        message: 'Invalid request. Please check your selection and try again.',
        statusCode,
      };
    }

    if (statusCode === 404) {
      return {
        message:
          'Review request not found. It may have expired or already been processed.',
        statusCode,
      };
    }

    if (statusCode === 500) {
      return {
        message: 'Server error occurred. Please try again later.',
        statusCode,
      };
    }

    if (error.code === 'ECONNABORTED') {
      return {
        message:
          'Request timed out. Please check your connection and try again.',
      };
    }

    if (!error.response) {
      return {
        message: 'Network error. Please check your internet connection.',
      };
    }

    return {
      message: error.response?.data?.message || 'An unexpected error occurred.',
      statusCode,
    };
  }

  return {
    message: 'An unexpected error occurred while submitting your decision.',
  };
}


export default vendorReviewService;
